import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Facebook, Instagram, Linkedin, Globe, Mail } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-navy-900 text-white">
      {/* Prominent Company Name Header */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img 
              src="/assets/logo.png"
              alt="Aquarist Pools and Fitness Logo"
              width="56"
              height="56"
              loading="lazy"
              decoding="async"
              className="h-14 w-auto"
            />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              Aquarist Pools & Fitness
            </h2>
          </div>
          <p className="text-[#00AEEF] font-semibold text-base mb-2">Complete Pool & Spa Solutions</p>
          <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
            <Globe className="w-4 h-4 text-[#00AEEF]" />
            Bangalore Headquarters — Serving Bangalore, Karnataka & Pan India
          </p>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About */}
          <div>
            <h4 className="text-lg font-semibold mb-5">About Us</h4>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Bangalore's trusted swimming pool construction company. Expert pool builders offering luxury pool construction, renovation, waterproofing, AMC, jacuzzi installation, and pipeless filtration across Bangalore, Karnataka, and pan India.
            </p>
            <div className="flex gap-3">
              <a 
                href="https://www.facebook.com/people/Aquarist-Pools-and-Fitness/61555539641106/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-blue-600 flex items-center justify-center transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://www.instagram.com/aquaristpools/?utm_source=qr&igsh=MWp6M3JodDd2a2x4aA%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-pink-600 flex items-center justify-center transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://www.linkedin.com/in/aquarist-pools-and-fitness-06ba832aa/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-blue-700 flex items-center justify-center transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Home</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">About Us</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Services</Link></li>
              <li><Link to="/pipeless-filtration" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Pipeless Filtration</Link></li>
              <li><Link to="/why-choose-us" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Why Choose Us</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Contact</Link></li>
              <li><Link to="/swimming-pool-construction" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Swimming Pool Construction</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Our Services</h4>
            <ul className="space-y-3">
              <li><Link to="/services" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Pool Design & Construction</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Pool Maintenance & AMC</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Pool Renovation & Repairs</Link></li>
              <li><Link to="/pipeless-filtration" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Pipeless Filtration Systems</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">Jacuzzi Massage Bathtubs</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">SOPREMAPOOL Liner</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-400 text-sm">
                    Opposite to Accent Ashlar Apartment,<br />
                    Chikkanagamangala, Huskur Rd,<br />
                    Electronic City, Bengaluru 560099
                  </span>
                  <a
                    href="https://www.google.com/maps/search/?api=1https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery=Aquarist+Pools+and+Fitnesshttps://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-get-directions"
                    className="flex items-center gap-1 text-[#00AEEF] hover:text-[#00d4ff] text-sm font-semibold mt-2 transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> Get Directions (One Click)
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#00AEEF] flex-shrink-0" />
                <a href="tel:+917757999805" className="text-gray-400 hover:text-[#00AEEF] transition-colors text-sm">
                  077579 99805
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <a href="mailto:aquarist.pools@gmail.com" className="text-gray-400 hover:text-[#00AEEF] transition-colors block">aquarist.pools@gmail.com</a>
                  <a href="mailto:admin@aquaristpools.com" className="text-gray-400 hover:text-[#00AEEF] transition-colors block">admin@aquaristpools.com</a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Footer Lead Capture */}
      <div className="border-t border-white/10 bg-gradient-to-r from-[#003366] to-[#001a33]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Ready to Build Your Dream Pool?</h3>
              <p className="text-gray-400 text-sm">Speak to Bangalore's top pool construction experts. Free consultation, transparent pricing.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a
                href="https://wa.me/917757999805?text=Hi%20Aquarist%20Pools%20and%20Fitness%2C%20I%20would%20like%20to%20enquire%20about%20swimming%20pool%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm"
                data-testid="footer-lead-whatsapp"
              >
                WhatsApp Us
              </a>
              <a
                href="tel:+917757999805"
                className="flex items-center gap-2 bg-[#00AEEF] hover:bg-[#0098d4] text-white font-semibold px-6 py-3 rounded-xl transition-all text-sm"
                data-testid="footer-lead-call"
              >
                <Phone className="w-4 h-4" /> Call +91 77579 99805
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-gray-500 text-xs leading-relaxed">
            Swimming Pool Construction Bangalore | Pool Builders Bangalore | Luxury Swimming Pools Bangalore | Pool Waterproofing Experts Bangalore | Pool Renovation Bangalore | Commercial Pool Construction India | Swimming Pool AMC Bangalore | Jacuzzi and Sauna Installation Bangalore | Serving: Whitefield, Electronic City, Sarjapur, Koramangala, JP Nagar, HSR Layout, Yelahanka, Hebbal, Indiranagar, Mysore, Mangalore, Hubli — All of Karnataka
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              &copy; {currentYear} <strong className="text-white">Aquarist Pools & Fitness</strong>. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-[#00AEEF] transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-[#00AEEF] transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>

      {/* Organisation Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Aquarist Pools & Fitness",
            "alternateName": "Aquarist Pools and Fitness",
            "url": "https://www.aquaristpools.com",
            "logo": "https://www.aquaristpools.com/assets/logo.png",
            "description": "Bangalore's leading swimming pool construction company. Expert pool builders offering luxury pool construction, renovation, waterproofing, AMC, jacuzzi & sauna installation, and pipeless filtration systems across Bangalore, Karnataka, and pan India.",
            "email": "aquarist.pools@gmail.com",
            "telephone": "+917757999805",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Opposite to Accent Ashlar Apartment, Chikkanagamangala, 2, Huskur Rd",
              "addressLocality": "Electronic City, Bengaluru",
              "addressRegion": "Karnataka",
              "postalCode": "560099",
              "addressCountry": "IN"
            },
            "areaServed": [
              {"@type": "City", "name": "Bangalore"},
              {"@type": "State", "name": "Karnataka"},
              {"@type": "Country", "name": "India"}
            ],
            "sameAs": [
              "https://www.facebook.com/people/Aquarist-Pools-and-Fitness/61555539641106/",
              "https://www.instagram.com/aquaristpools/",
              "https://www.linkedin.com/in/aquarist-pools-and-fitness-06ba832aa/"
            ]
          })
        }}
      />
    </footer>
  );
};

export default Footer;
